/**
 * Allys Phone Input Component
 * 
 * Modern phone number input with country selector
 * Following Allys theme styling with Inter font
 */

'use client';

import { Phone } from '@phosphor-icons/react';
import { useState } from 'react';

interface Country {
  code: string;
  name: string;
  dial: string;
  flag: string;
}

const countries: Country[] = [
  { code: 'MU', name: 'Mauritius', dial: '+230', flag: '🇲🇺' },
  { code: 'US', name: 'United States', dial: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { code: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', dial: '+49', flag: '🇩🇪' },
  { code: 'IN', name: 'India', dial: '+91', flag: '🇮🇳' },
  { code: 'ZA', name: 'South Africa', dial: '+27', flag: '🇿🇦' },
  { code: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
  { code: 'AE', name: 'UAE', dial: '+971', flag: '🇦🇪' },
];

export interface AllysPhoneInputProps {
  id?: string;
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  defaultCountry?: string;
}

export function AllysPhoneInput({
  id,
  name,
  value = '',
  onChange,
  placeholder = 'Phone number',
  required = false,
  error,
  defaultCountry = 'MU',
}: AllysPhoneInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country>(() => {
    const found = countries.find(c => c.code === defaultCountry);
    return found || countries[0] || { code: 'MU', name: 'Mauritius', dial: '+230', flag: '🇲🇺' };
  });
  const [phoneNumber, setPhoneNumber] = useState(value);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsOpen(false);
    // Update full value with new country code
    if (onChange) {
      onChange(`${country.dial} ${phoneNumber}`);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const number = e.target.value;
    setPhoneNumber(number);
    if (onChange) {
      onChange(`${selectedCountry.dial} ${number}`);
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        {/* Country Selector */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`
              h-[46px] px-3 flex items-center gap-2
              border ${error ? 'border-red-500' : 'border-gray-200'}
              rounded-lg
              hover:border-gray-900
              focus:border-gray-900 focus:outline-none
              transition-colors duration-200
              bg-white
              font-inter
            `}
          >
            <span className="text-xl">{selectedCountry.flag}</span>
            <span className="text-sm text-gray-900 font-normal">{selectedCountry.dial}</span>
            <svg 
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown */}
          {isOpen && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setIsOpen(false)}
              />
              <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
                {countries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    className={`
                      w-full px-4 py-3 flex items-center gap-3 text-left
                      hover:bg-gray-50 transition-colors duration-150
                      font-inter
                      ${selectedCountry.code === country.code ? 'bg-gray-50' : ''}
                    `}
                  >
                    <span className="text-xl">{country.flag}</span>
                    <span className="text-sm text-gray-900 font-normal flex-1">{country.name}</span>
                    <span className="text-sm text-gray-500 font-normal">{country.dial}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="flex-1 relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none z-10">
            <Phone size={20} weight="light" className="text-gray-400" />
          </div>
          <input
            type="tel"
            id={id}
            name={name}
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            required={required}
            className={`
              w-full pl-11 pr-4 py-3
              border ${error ? 'border-red-500' : 'border-gray-200'}
              rounded-lg
              focus:border-gray-900 focus:outline-none
              transition-colors duration-200
              text-gray-900 placeholder:text-gray-400
              bg-white
              font-inter
            `}
          />
        </div>
      </div>
      
      {error && (
        <p 
          className="mt-2 text-sm text-red-500 font-inter"
        >
          {error}
        </p>
      )}
    </div>
  );
}

